import { states } from "#lib/address.js";

export const PRECONFIGURED_OPTIONS = [
	{ label: "US States", key: "us_states" },
	{ label: "US States abbreviated", key: "us_states_abbreviated" },
	{ label: "Countries", key: "countries" },
	{ label: "Months", key: "months" },
	{ label: "Timezones", key: "timezones" },
] as const;

export type PreconfiguredOptionKey = (typeof PRECONFIGURED_OPTIONS)[number]["key"];

export function getPreconfiguredOptions(key: PreconfiguredOptionKey): string[] {
	switch (key) {
		case "us_states":
			return states.map(state => state.name);
		case "us_states_abbreviated":
			return states.map(state => state.value);
		case "countries":
			return getCountries();
		case "months":
			return getMonths();
		case "timezones":
			return [...Intl.supportedValuesOf("timeZone")].sort((a, b) => a.localeCompare(b, "en"));
	}
}

const NON_COUNTRY_REGION_CODES = new Set([
	// continents/political/economic groupings, unassigned placeholders, CLDR
	// pseudo-locale test regions, and exceptionally-reserved territory aliases
	"AC",
	"CP",
	"DG",
	"EA",
	"EU",
	"EZ",
	"FX",
	"IC",
	"QO",
	"TA",
	"UK",
	"UN",
	"XA",
	"XB",
	"ZZ",
	// deprecated codes that only resolve to a successor country under ICU alias
	// resolution, and render as the defunct state on older ICU builds
	"AN",
	"BU",
	"CS",
	"DD",
	"DY",
	"HV",
	"NH",
	"RH",
	"SU",
	"TP",
	"VD",
	"YD",
	"YU",
	"ZR",
	// not ISO 3166-1 assigned
	"CQ",
	"XK",
]);

function getCountries(): string[] {
	const displayNames = new Intl.DisplayNames("en", { type: "region" });
	const names: string[] = [];
	for (let first = 65; first <= 90; first++) {
		for (let second = 65; second <= 90; second++) {
			const code = String.fromCodePoint(first, second);
			if (NON_COUNTRY_REGION_CODES.has(code)) {
				continue;
			}
			const name = displayNames.of(code);
			if (name && name !== code) {
				names.push(name);
			}
		}
	}
	return [...new Set(names)].sort((a, b) => a.localeCompare(b, "en"));
}

function getMonths(): string[] {
	const formatter = new Intl.DateTimeFormat("en-US", { month: "long" });
	return Array.from({ length: 12 }, (_, month) => formatter.format(new Date(2020, month, 1)));
}
